export type ThrottledFunction<T extends (...args: any[]) => any> = (
  ...args: Parameters<T>
) => ReturnType<T> | undefined;

/**
 * Creates a throttled function that limits how often the original function can be called.
 * 
 * Uses a leading + trailing approach:
 * - The first call executes immediately (leading)
 * - Subsequent calls during the throttle period update the arguments
 * - If there were calls during the period, executes once more at the end with the latest arguments (trailing)
 * - If no calls were made during the throttle period, no trailing execution occurs
 * 
 * @template T The type of the function to throttle
 * @param callback The function to throttle
 * @param delay The number of milliseconds to throttle invocations to
 * @returns A new throttled function that maintains the same signature as the original
 * @warning This function does not preserve "this" context, so you can't call throttled functions defined on objects
 * 
 * @example
 * ```typescript
 * const throttledSave = throttle((data: string) => saveToServer(data), 1000);
 * 
 * throttledSave('data1'); // Executes immediately
 * throttledSave('data2'); // Queued for trailing execution
 * throttledSave('data3'); // Updates queued arguments
 * // After 1000ms: executes with 'data3'
 * ```
 */
export const throttle = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): ThrottledFunction<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastResult: ReturnType<T> | undefined;
  let hasPendingCall = false; // tracks if there were calls during throttle period

  const executeTrailing = () => {
    timeoutId = null;
    
    if (hasPendingCall && lastArgs) {
      // there were calls during the throttle period -> execute the trailing call
      const args = lastArgs;
      lastArgs = null;
      hasPendingCall = false;
      
      lastResult = callback(...args) as ReturnType<T>;
    } else {
      // reset state
      lastArgs = null;
      hasPendingCall = false;
    }
  };

  return (...args: Parameters<T>): ReturnType<T> | undefined => {
    lastArgs = args;

    if (!timeoutId) {
      // leading call -> execute immediately and start the throttle period
      lastResult = callback(...args) as ReturnType<T>;
      hasPendingCall = false; // reset since we just executed
      timeoutId = setTimeout(executeTrailing, delay);
    } else {
      // for subsequent call during throttle period, register that we have been called
      hasPendingCall = true;
    }
    
    return lastResult;
  };
};